import { DottedLineIcon } from '@raysee/icon';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { Bubble, Props as BubbleProps } from './Bubble';
import { LoadingBubble } from './LoadingBubble';

interface Props extends BubbleProps {
  selectedChoice?: string;
  disableContext?: boolean;
  scrollToBottom: () => void;
}

const getProblemContent = (problem: string) =>
  ({
    /* -- 恋愛/男女関係の悩み① -- */
    片想い: 'あなたのことをどう思っているのか、２人の今後について',
    '恋人との今後/結婚': 'あなたのことをどう思っているのか、２人の今後について',
    '婚期/婚活':
      '結婚相手との出会い、あなたの婚期について <p style="font-weight: 500;">*特定のお相手についてのご相談は「恋人との今後/結婚」へ</p>',
    出会い: '異性との出会いや運命の人がいつ現れるかについて',
    /* -- 恋愛/男女関係の悩み② -- */
    夫婦生活:
      '旦那様、奥様との相性や今後の関係について <p style="font-weight: 500;">*子宝については占えません</p>',
    復縁: '破局してしまったお相手とのご縁について',
    '妻/彼女持ちの彼': 'パートナーがいるお相手の本音と今後について',
    W不倫: 'パートナーがいるお相手の本音と今後について',
    セックスレス: 'パートナーとの性生活、相手の本音やコミュニケーションについて',
    /* -- 恋愛/夫婦の悩み -- */
    夫婦関係の修復: 'すれ違ってしまった原因と本心、二人の関係の戻し方について',
    パートナーの浮気: '浮気をしたパートナーの本心と今後の向き合い方について',
    離婚したい: '離婚の是非や別れたあとの未来について',
    夫婦生活の不安:
      'パートナーとの相性や今後の夫婦生活について <p style="font-weight: 500;">*子宝については占えません</p>',
    /* -- 人間関係の悩み -- */
    職場: '職場内での人間関係について',
    友人: '特定の友人や身の周りの友人関係などについて',
    家族: 'お子様や家族全体の運勢と今後について <p style="font-weight: 500;">*子宝については占えません</p>',
    義家族: '義家族との相性や今後について',
    /* -- 仕事の悩み -- */
    '給料/評価': '今後の給料や会社での評価について',
    事業の成否: '今後始める事業の運気について',
    '独立/開業': '独立や開業をした際の将来について',
    '転職/キャリアチェンジ': '転職時期や職種、その後の将来について',
    就職: 'あなたに合う職種や就活の運気などについて',
    /* -- お金の悩み -- */
    借金がある: 'あなたの借金の今後の流れについて',
    貯金ができない: '貯金をするための方法について',
    将来の不安: 'あなたの将来の金運について',
    もっと稼ぎたい: '今より収入を増やす方法について',
    総合金運: 'この先、数年間の金運について',
  })[problem];

export const CarouselBubble = (props: Props) => {
  const maxChoices = Math.max(...props.message.carousels?.map((carusel) => carusel.choices.length));
  const selectedCarousel = props.selectedChoice
    ? props.message.carousels.find((carousel) => carousel.choices.includes(props.selectedChoice))
    : null;
  const [isShow, setIsShow] = useState(!props.isWaiting);

  useEffect(() => {
    if (isShow) {
      if (props.isWaiting) props.scrollToBottom();
      return;
    }

    const timer = setTimeout(() => {
      setIsShow(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, [isShow]);

  return (
    <>
      <Bubble {...props}>
        <div className="whitespace-pre-line rounded-2xl border bg-white px-4 py-3 drop-shadow-sm">
          <p>{props.message.text}</p>
        </div>
      </Bubble>
      {!isShow ? (
        <LoadingBubble sender={props.sender} />
      ) : isShow && props.isWaiting && !props.selectedChoice ? (
        <div className="my-4">
          <div
            className={`flex overflow-x-auto pb-5 ${
              props.message.carousels.length === 1 && 'justify-center'
            }`}
          >
            {props.message.carousels.map((carousel, i) => (
              <div
                className="mx-1 w-60 flex-none rounded-3xl bg-white shadow-xl first:ml-4 last:mr-4"
                key={i}
              >
                <Image
                  src={carousel.imageUrl}
                  width="241"
                  height="122"
                  alt=""
                  className="rounded-t-3xl"
                />
                {carousel.description && (
                  <p className="p-2.5 text-justify text-xs font-semibold">{carousel.description}</p>
                )}
                <div className="mx-2 border-[rgb(72,59,67)]">
                  {carousel.choices.map((choice, j) => (
                    <div key={j} className="text-center">
                      {j !== 0 && <div className="border-t border-dotted border-blue-300" />}
                      <button
                        className="h-12 w-full whitespace-pre-line text-blue-400"
                        onClick={() => props.onSend(choice)}
                      >
                        {choice}
                      </button>
                    </div>
                  ))}
                  {[...Array(maxChoices - carousel.choices.length)].map((k) => (
                    <div key={k} className="h-[49px]" />
                  ))}
                </div>
                {!props.isWaiting && (
                  <div className="relative -top-full h-full w-full rounded-2xl bg-black opacity-[0.45]" />
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        selectedCarousel && (
          <div className="mx-auto w-60 flex-none rounded-3xl bg-white shadow-xl">
            <Image
              src={selectedCarousel.imageUrl}
              width="241"
              height="179"
              alt=""
              className="rounded-t-3xl"
            />
            <div className="mx-2 mt-[-6px] border-t border-dotted border-[rgb(72,59,67)]">
              <div className="text-center">
                <button
                  className="w-full whitespace-pre-line py-3 font-bold text-blue-400"
                  disabled
                >
                  {props.selectedChoice}
                </button>
              </div>
              {!props.disableContext && getProblemContent(props.selectedChoice) && (
                <>
                  <DottedLineIcon className="m-auto w-full" />
                  <p
                    dangerouslySetInnerHTML={{ __html: getProblemContent(props.selectedChoice) }}
                    className="m-auto px-3 pb-6 pt-4 text-justify text-xs font-semibold"
                  />
                </>
              )}
            </div>
          </div>
        )
      )}
    </>
  );
};
